import { useCallback } from "react";

import { paywallCheckoutApi } from "@/react/api/paywallCheckoutApi";

import { CheckoutError } from "../../CheckoutError";

interface CreateAttemptResponseBody {
  checkout_attempt_id: number;
  deferred_setup: boolean;
  invoice_processor_id?: string;
  payment_intent_client_secret?: string;
  payment_intent_processor_id?: string;
}

export interface Attempt {
  id: number;
  invoiceId?: string;
  isSetupDeferred: boolean;
  paymentIntentClientSecret?: string;
  paymentIntentId?: string;
}

export interface UseCreateAttemptProps {
  checkoutIntentToken?: string;
  communityId: number;
  externalTransactionToken?: string;
  paymentMethodType: string;
  paywallPriceId: number;
  sessionStepId?: number;
}

export const useCreateAttempt = ({
  checkoutIntentToken,
  communityId,
  externalTransactionToken,
  paymentMethodType,
  paywallPriceId,
  sessionStepId,
}: UseCreateAttemptProps) => {
  const createAttempt = useCallback(
    async (data: Record<string, unknown>) => {
      const response = await paywallCheckoutApi.create({
        formData: {
          ...data,
          // Default to "card" when paymentMethodType is empty (e.g., when address element
          // is not required and payment method type detection hasn't fired). This matches
          // legacy behavior and ensures backend validation passes for off-session usage.
          payment_method_type: paymentMethodType || "card",
          community_id: communityId,
          paywall_price_id: paywallPriceId,
          checkout_session_step_id: sessionStepId,
          checkout_intent_token: checkoutIntentToken,
          // Google Play ECL: forwarded on every create call (payment-intent
          // and deferred-setup paths alike) so the resulting charge can later
          // be attributed to Google. Captured once per checkout session --
          // see PaywallCheckoutAttemptsContextProvider.
          external_transaction_token: externalTransactionToken,
        },
      });

      if (!response || !response.ok) {
        const error = await response?.json?.();
        throw CheckoutError.fromPaywallError(
          "useCreateAttempt.createAttempt",
          error,
        );
      }

      const responseBody = (await response.json()) as CreateAttemptResponseBody;
      const isSetupDeferred = responseBody.deferred_setup;
      return {
        id: responseBody.checkout_attempt_id,
        invoiceId: responseBody.invoice_processor_id,
        isSetupDeferred,
        paymentIntentClientSecret: responseBody.payment_intent_client_secret,
        paymentIntentId: responseBody.payment_intent_processor_id,
      } as Attempt;
    },
    [
      checkoutIntentToken,
      communityId,
      externalTransactionToken,
      paymentMethodType,
      paywallPriceId,
      sessionStepId,
    ],
  );
  return createAttempt;
};
